import { useState, useRef, useEffect } from "react";
import GetIcon from "../../../../utils/Icon";
import ApiResponseBox from "../../../../Components/ApiResponseBox";
import Pagination from "../../../../Components/Pagination";
import Button from "../../../../Core/Button/Button";


const Table = ({ item_Rows, onSplitItem, blinkrow }) => {
  const tableRef = useRef(null);
  const [error, setError] = useState('');
  const [rowsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [showError, setShowError] = useState(false);
  const [showColumnConfig, setShowColumnConfig] = useState(false);
  const indexOfLastRow = currentPage * rowsPerPage;
  const indexOfFirstRow = indexOfLastRow - rowsPerPage;
  const currentRows = item_Rows.slice(indexOfFirstRow, indexOfLastRow);
  const initialColumns = [
    // { key: 'action', name: 'Action', width: '5%', selected: true },
    { key: 'sno', name: 'No.', width: '4%', selected: true },
    { key: 'item_code', name: 'Item Code', width: '13%', selected: true },
    { key: 'item_name', name: 'Item Name', width: '22%', selected: true },
    { key: 'received_qty', name: 'Order Qty', width: '5%', selected: true },
    { key: 'new_bin', name: 'Bin', width: '12%', selected: true },
    { key: 'scan_received_qty', name: 'Scan Qty', width: '5%', selected: true },
    { key: 'rate', name: 'Rate', width: '10%', selected: true },
    { key: 'amount', name: 'Amount', width: '10%', selected: true },
    { key: 'setting', name: 'Setting', width: '4%', selected: true },
  ];

  const [columns, setColumns] = useState(initialColumns);
  const visibleColumns = columns.filter(col => col.selected);

  const getRowColor = (row) => {
    const itemRows = item_Rows.filter(r => r.item_code === row.item_code);
    const totalScanQty = itemRows.reduce((sum, r) => sum + Number(r.scan_qty), 0);
    if (totalScanQty === 0) return 'not-distributed';
    if (totalScanQty >= row.received_qty) return 'fully-distributed';
    return 'partially-distributed';
  };

  const handleSaveColumns = (updatedColumns) => {
    setColumns(updatedColumns);
    setShowColumnConfig(false);
  };

  const openColumnConfig = () => {
    setShowColumnConfig(true);
  };

  useEffect(() => {
    const table = tableRef.current;
    if (!table) return;

    const cols = table.querySelectorAll("th");
    const mouseDownHandlers = new Map();

    cols.forEach((col) => {
      const existingResizer = col.querySelector(".resizer");
      if (existingResizer) {
        col.removeChild(existingResizer);
      }

      const resizer = document.createElement("div");
      resizer.className = "resizer";
      col.appendChild(resizer);

      let x = 0;
      let w = 0;

      const mouseDownHandler = (e) => {
        x = e.clientX;
        w = col.getBoundingClientRect().width;
        document.addEventListener("mousemove", mouseMoveHandler);
        document.addEventListener("mouseup", mouseUpHandler);
        col.style.userSelect = "none";
        col.style.cursor = "col-resize";
      };

      const mouseMoveHandler = (e) => {
        const dx = e.clientX - x;
        const newWidth = w + dx;

        if (newWidth < 50) return;

        const originalWidth = col.style.width || col.getAttribute('data-original-width');
        const isPercentage = originalWidth.includes('%');

        const widthValue = isPercentage
          ? `${(newWidth / table.getBoundingClientRect().width) * 100}%`
          : `${newWidth}px`;

        col.style.width = widthValue;

        const colIndex = Array.from(cols).indexOf(col);
        const cells = table.querySelectorAll(`td:nth-child(${colIndex + 1})`);
        cells.forEach((cell) => {
          cell.style.width = widthValue;
        });
      };

      const mouseUpHandler = () => {
        document.removeEventListener("mousemove", mouseMoveHandler);
        document.removeEventListener("mouseup", mouseUpHandler);
        col.style.userSelect = "";
        col.style.cursor = "";
      };

      mouseDownHandlers.set(col, mouseDownHandler);
      col.setAttribute('data-original-width', col.style.width || columns[col.dataset.index]?.width || '100px');
      resizer.addEventListener("mousedown", mouseDownHandler);
    });

    return () => {
      cols.forEach((col) => {
        const resizer = col.querySelector(".resizer");
        if (resizer) {
          const handler = mouseDownHandlers.get(col);
          if (handler) {
            resizer.removeEventListener("mousedown", handler);
          }
        }
      });
    };
  }, [item_Rows, currentRows]);

  return (
    <>
      <table className="table table-bordered mb-1" ref={tableRef}>
        <colgroup>
          {visibleColumns.map((col) => (
            <col
              key={col.key}
              style={{ width: col.width }}
              data-original-width={col.width}
            />
          ))}
        </colgroup>
        <thead className="tableHead1">
          <tr>
            {visibleColumns.map((col, index) => (
              <th
                key={col.key}
                data-index={index}
                style={{
                  width: col.width,
                  minWidth: col.width,
                  position: "relative"
                }}
              >
                {col.key === "setting" ? (
                  <div onClick={openColumnConfig} style={{ cursor: 'pointer' }}>
                    <GetIcon iconName="IoSettingsOutline" size={13} />
                  </div>
                ) : (
                  col.name
                )}
                <div className="resizer" />
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {currentRows.map((row, index) => (
            <tr key={index}>
              {columns.map((col) => {
                if (col.key === 'sno') {
                  return <td key="sno">{indexOfFirstRow + index + 1}</td>;
                } else if (col.key === 'new_bin') {
                  return (
                    <td key="new_bin">
                      {row.t_warehouse || <span className="text-muted">Not scanned</span>}
                    </td>
                  );
                } else if (col.key === 'rate') {
                  return <td key="rate">{`₹${row.valuation_rate}`}</td>;
                } else if (col.key === 'amount') {
                  return (
                    <td key="amount">
                      {row.scan_qty == 0
                        ? `₹${row.valuation_rate * row.grn_qty}`
                        : `₹${row.valuation_rate * row.scan_received_qty}`}
                    </td>
                  );
                } else if (col.key === 'qty') {
                  return <td key="qty">{row.qty}</td>;
                } else if (col.key === 'scan_received_qty') {
                  return <td key="scan_received_qty">{row.scan_received_qty}</td>;
                } else if (col.key === 'setting') {
                  return (
                    <td key="setting">
                      <span>
                        <GetIcon iconName="MdOutlineEdit" size={15} />
                      </span>
                    </td>
                  );
                } else {
                  return <td key={col.key}>{row[col.key]}</td>;
                }
              })}
            </tr>
          ))}
        </tbody>

      </table>

      <div className="d-flex pb-2 justify-content-between align-items-center">
        <div className="d-flex" style={{ gap: '4px' }}>
          <Button
            className="createbtn d-flex"
            style={{ gap: '2px', height: '28px' }}
            iconName="MdOutlineFileDownload"
            iconSize={12}
          >
            Download
          </Button>
        </div>

        <div className={`${item_Rows.length < 11 ? "d-none" : "d-flex"} align-items-center centerpagination`} style={{ gap: '5px' }}>
          <Pagination
            setCurrentPage={setCurrentPage}
            currentPage={currentPage}
            pageSize={rowsPerPage}
            totalrecords={item_Rows.length}
          />
        </div>

        <div className={`${item_Rows.length < 11 ? "d-none" : "d-flex"}`}>
          <span className="px-2" style={{ fontSize: '12px', color: '#270B79' }}>
            Showing {indexOfFirstRow + 1} to {Math.min(indexOfLastRow, item_Rows.length)} of {item_Rows.length} entries
          </span>
        </div>
      </div>



      <ApiResponseBox show={showError} errorMessage={error} onClose={() => setShowError(false)} />
    </>
  );
};

export default Table;